import type { HubItem, HubItemInput, Attachment, DashboardData } from '@/types'

const BASE = '/api'

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    ...init,
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error((err as { error?: string }).error ?? res.statusText)
  }
  if (res.status === 204) return undefined as T
  return res.json()
}

// Items
export const api = {
  dashboard: (): Promise<DashboardData> =>
    request('/dashboard'),

  items: {
    list: (category?: string): Promise<HubItem[]> =>
      request(`/items${category ? `?category=${category}` : ''}`),

    get: (id: string): Promise<HubItem> =>
      request(`/items/${id}`),

    create: (data: HubItemInput): Promise<HubItem> =>
      request('/items', { method: 'POST', body: JSON.stringify(data) }),

    update: (id: string, data: Partial<HubItemInput>): Promise<HubItem> =>
      request(`/items/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

    delete: (id: string): Promise<void> =>
      request(`/items/${id}`, { method: 'DELETE' }),
  },

  attachments: {
    list: (itemId: string): Promise<Attachment[]> =>
      request(`/items/${itemId}/attachments`),

    upload: async (itemId: string, file: File): Promise<Attachment> => {
      const form = new FormData()
      form.append('file', file)
      const res = await fetch(`${BASE}/items/${itemId}/attachments`, {
        method: 'POST',
        body: form,
      })
      if (!res.ok) throw new Error(await res.text())
      return res.json()
    },

    linkOneDrive: (itemId: string, file: { id: string; driveId: string; name: string; webUrl: string; size?: number; mimeType?: string }): Promise<Attachment> =>
      request(`/items/${itemId}/attachments/onedrive`, {
        method: 'POST',
        body: JSON.stringify(file),
      }),

    delete: (itemId: string, attachmentId: string): Promise<void> =>
      request(`/items/${itemId}/attachments/${attachmentId}`, { method: 'DELETE' }),

    downloadUrl: (itemId: string, attachmentId: string): string =>
      `${BASE}/items/${itemId}/attachments/${attachmentId}/download`,
  },

  onedrive: {
    status: (): Promise<{ connected: boolean; email?: string }> =>
      request('/onedrive/status'),

    files: (driveId?: string, itemId?: string): Promise<{ files: import('@/types').OneDriveFile[]; driveId: string }> =>
      request(`/onedrive/files${driveId ? `?driveId=${driveId}&itemId=${itemId ?? 'root'}` : ''}`),

    disconnect: (): Promise<void> =>
      request('/onedrive/disconnect', { method: 'POST' }),
  },
}
